/* eslint-disable react-hooks/exhaustive-deps */
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    EnvironmentOutlined,
    FieldTimeOutlined,
    PlusOutlined,
    WhatsAppOutlined,
} from '@ant-design/icons';
import {
    Avatar,
    Button,
    Card,
    Col,
    Divider,
    Flex,
    Image,
    Popconfirm,
    Row,
    Segmented,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import clsx from 'clsx';
import { Building, Coins, Eye, FileText, Mail, Phone, RefreshCcw, UserRound } from 'lucide-react';
import { Activity, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BalanceTag from '../../../components/BalanceTag';
import { PLACEHOLDER_IMAGE } from '../../../constants';
import {
    deleteCustomerSuggestedStatus,
    getCustomerDetail,
    getCustomerSpecialPrices,
    getCustomerSuggestedPrices,
    updateCustomerSuggestedStatus,
} from '../../../redux/actions/customers';
import { getRoutes, ROLES } from '../../../routes/Routes';
import { formatAddress, formatCurrency, getAddressType, getInitials, getProductImageUrl } from '../../../utils';
import SuggestedPrice from './SuggestedPrice';

const { Title, Text } = Typography;

const CustomerDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const suggestedPrice = searchParams.get('suggested-price') === 'true';
    const { customerId } = useParams();

    const { user } = useSelector((state) => state.authSlice);
    const { specialPrices, suggestedPrices } = useSelector((state) => state.customersSlice);

    const [customer, setCustomer] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState({
        customer: false,
        specialPrices: false,
        suggestedPrices: false,
    });
    const [priceType, setPriceType] = useState(suggestedPrice ? 2 : 1);
    const [showThumbnail, setShowThumbnail] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [suggestedData, setSuggestedData] = useState(null);

    const data = priceType === 1 ? specialPrices : suggestedPrices;

    const getCustomer = async (id) => {
        try {
            setLoading((prev) => ({ ...prev, customer: true }));
            const res = await getCustomerDetail(id);
            if (res?.ack) {
                if (suggestedPrice) {
                    getSuggestedPrices(id);
                } else {
                    getSpecialPrices(id);
                }
                setCustomer(res.customer_details?.at(0));
                setBalance(res.customer_balance);
                const fullAddress = res.customer_details?.map((item) => {
                    const { fullAddress } = formatAddress(item);
                    return { ...item, fullAddress };
                });
                setAddresses([...fullAddress]);
            } else {
                navigate(getRoutes().customers.route());
            }
        } finally {
            setLoading((prev) => ({ ...prev, customer: false }));
        }
    };

    const getSpecialPrices = async (id) => {
        try {
            setLoading((prev) => ({ ...prev, specialPrices: true }));
            await getCustomerSpecialPrices(id);
        } finally {
            setLoading((prev) => ({ ...prev, specialPrices: false }));
        }
    };

    const getSuggestedPrices = async (id) => {
        try {
            setLoading((prev) => ({ ...prev, suggestedPrices: true }));
            await getCustomerSuggestedPrices(id);
        } finally {
            setLoading((prev) => ({ ...prev, suggestedPrices: false }));
        }
    };

    const handlePriceTypeChange = (value) => {
        setPriceType(value);
        if (value === 1) {
            getSpecialPrices(customerId);
        } else if (value === 2) {
            getSuggestedPrices(customerId);
        }
    };

    const handleStatusChange = async (record, status) => {
        const res = await updateCustomerSuggestedStatus({
            id: record.id,
            status,
        });
        if (res?.ack) {
            getSuggestedPrices(customerId);
        }
    };

    const columns = [
        {
            dataIndex: 'thumbnail',
            key: 'thumbnail',
            align: 'center',
            width: 60,
            hidden: !showThumbnail,
            render: (_, record) => {
                const productImg = getProductImageUrl(record);
                const items = data?.map((item) => getProductImageUrl(item) || PLACEHOLDER_IMAGE);

                return (
                    <Image.PreviewGroup items={items}>
                        <Image
                            src={productImg}
                            alt={record?.product_name}
                            width={50}
                            height={50}
                            fallback={PLACEHOLDER_IMAGE}
                            preview={{
                                mask: <Eye size={16} />,
                            }}
                        />
                    </Image.PreviewGroup>
                );
            },
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Price Level',
            dataIndex: 'price_level',
            key: 'price_level',
        },
        {
            title: 'Price',
            dataIndex: 'unit_price',
            key: 'unit_price',
            render: (text, record) => formatCurrency(text || record?.price),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            hidden: priceType !== 2,
            render: (text) => {
                const statusMap = {
                    0: { label: 'Pending', color: 'orange-inverse' },
                    1: { label: 'Approved', color: 'green-inverse' },
                    2: { label: 'Rejected', color: 'red-inverse' },
                };

                return <Tag color={statusMap[text || 0]?.color}>{statusMap[text || 0]?.label}</Tag>;
            },
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            align: 'end',
            hidden: priceType !== 2,
            render: (_, record) => (
                <Space>
                    <Activity
                        mode={
                            !record?.status && [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role)
                                ? 'visible'
                                : 'hidden'
                        }
                    >
                        <Space>
                            <Tooltip title="Approve">
                                <Popconfirm
                                    title="Approve Suggested Price"
                                    description="This action cannot be undone. Are you sure you want to approve this suggested price?"
                                    okText="Approve"
                                    cancelText="Cancel"
                                    okButtonProps={{
                                        color: 'green',
                                        variant: 'solid',
                                        size: 'medium',
                                    }}
                                    cancelButtonProps={{
                                        size: 'medium',
                                    }}
                                    classNames={{
                                        body: 'max-w-20rem',
                                    }}
                                    onConfirm={() => handleStatusChange(record, 1)}
                                >
                                    <Button variant="solid" color="green" icon={<CheckOutlined />}>
                                        Approve
                                    </Button>
                                </Popconfirm>
                            </Tooltip>
                            <Tooltip title="Reject">
                                <Popconfirm
                                    title="Reject Suggested Price"
                                    description="This action cannot be undone. Are you sure you want to reject this suggested price?"
                                    okText="Reject"
                                    cancelText="Cancel"
                                    okButtonProps={{
                                        color: 'danger',
                                        variant: 'solid',
                                        size: 'medium',
                                    }}
                                    cancelButtonProps={{
                                        size: 'medium',
                                    }}
                                    classNames={{
                                        body: 'max-w-20rem',
                                    }}
                                    onConfirm={() => handleStatusChange(record, 2)}
                                >
                                    <Button variant="solid" color="red" icon={<CloseOutlined />}>
                                        Reject
                                    </Button>
                                </Popconfirm>
                            </Tooltip>
                        </Space>
                    </Activity>
                    <Space>
                        <Tooltip title="Delete">
                            <Popconfirm
                                title="Delete Suggested Price"
                                description="This action cannot be undone. Are you sure you want to delete this suggested price?"
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{
                                    color: 'danger',
                                    variant: 'solid',
                                    size: 'medium',
                                }}
                                cancelButtonProps={{
                                    size: 'medium',
                                }}
                                classNames={{
                                    body: 'max-w-20rem',
                                }}
                                onConfirm={async () => {
                                    const res = await deleteCustomerSuggestedStatus({ id: record.id });
                                    if (res?.ack) {
                                        getCustomerSuggestedPrices(customerId);
                                    }
                                }}
                            >
                                <Button variant="solid" color="danger" icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <Button
                                variant="solid"
                                color="primary"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setSuggestedData(record);
                                    setIsModalOpen(true);
                                }}
                            />
                        </Tooltip>
                    </Space>
                </Space>
            ),
        },
    ];

    const customerInfo = [
        {
            label: 'Email',
            value: customer?.invoice_email || 'N/A',
            icon: <Mail size={14} />,
        },
        {
            label: 'Phone',
            value: customer?.phone || 'N/A',
            icon: <Phone size={14} />,
        },
        {
            label: 'WhatsApp',
            value: customer?.invoice_whatsapp_number || 'N/A',
            icon: <WhatsAppOutlined />,
        },
        {
            label: 'Sales Representative',
            value: customer?.salesrep || 'N/A',
            icon: <UserRound size={14} />,
        },
        {
            label: 'Price Level',
            value: customer?.price_level || 'N/A',
            icon: <Coins size={14} />,
        },
        {
            label: 'Terms',
            value: customer?.terms || 'N/A',
            icon: <FileText size={14} />,
        },
        {
            label: 'Business Hours',
            value: `${customer?.opening_time} → ${customer?.closing_time}`,
            icon: <FieldTimeOutlined />,
        },
        {
            label: 'Business Type',
            value:
                customer?.business_type?.split(',')?.map((type) => <Tag key={type}>{type}</Tag>) || 'N/A',
            icon: <Building size={14} />,
        },
    ];

    useEffect(() => {
        if (!customerId) return;
        getCustomer(customerId);
    }, [customerId]);

    return (
        <div className="customer-detail-page">
            {/* HEADER */}
            <Flex justify="space-between" align="center" gap={10} className="mb-3" wrap>
                <Space align="start" size={16}>
                    <Avatar
                        size={54}
                        shape="square"
                        style={{
                            background: 'linear-gradient(135deg, #4f8cff 0%, #6b5bff 100%)',
                        }}
                    >
                        {getInitials(customer?.title)}
                    </Avatar>

                    <div className="flex flex-column gap-1">
                        <Title level={3} className="page-title m-0" ellipsis={{ tooltip: true }}>
                            {customer?.title}
                        </Title>
                        <Text type="secondary">{customer?.code}</Text>
                    </div>
                </Space>
                <Space wrap>
                    <Tag color="gray" className="py-2 border-round-lg page-title">
                        Credit Limit: {formatCurrency(balance?.creditLimit || 0)}
                    </Tag>
                    <BalanceTag balance={balance} />
                </Space>
            </Flex>

            {/* BASIC INFO CARD */}
            <Card variant="borderless" className="p-0 border-round-2xl shadow-1">
                <Row gutter={[20, 20]}>
                    {customerInfo?.map((info) => (
                        <Col xs={24} md={12} key={info.label}>
                            <Text type="secondary">{info.label}</Text>
                            <div className="flex align-items-center gap-1">
                                {info.icon}
                                <Text>{info.value}</Text>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card>

            {/* ADDRESSES */}
            <Divider orientation="left" orientationMargin={0} className="my-3">
                {addresses?.length > 1 ? 'Addresses' : 'Address'}
            </Divider>

            <Row gutter={[16, 16]}>
                {!!addresses?.length ? (
                    addresses.map((addr, idx) => {
                        const addressType = getAddressType(addr);
                        return (
                            <Col xs={24} sm={12} key={addr.id}>
                                <Card
                                    variant="borderless"
                                    className="shadow-2 border-round-lg p-0"
                                    classNames={{
                                        body: 'p-3',
                                    }}
                                >
                                    <Space direction="vertical" size={4}>
                                        <Text strong>
                                            <EnvironmentOutlined /> {addressType}
                                        </Text>
                                        <Text type="secondary">{addr?.fullAddress}</Text>
                                    </Space>
                                </Card>
                            </Col>
                        );
                    })
                ) : (
                    <Col span={24} className="text-center">
                        <Text type="secondary">No address available.</Text>
                    </Col>
                )}
            </Row>

            {/* SPECIAL/SUGGESTED PRICES */}
            <Divider className="my-3" />

            <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <Segmented
                    options={[
                        { label: 'Special Prices', value: 1 },
                        { label: 'Suggested Prices', value: 2 },
                    ]}
                    defaultValue={priceType}
                    size="large"
                    onChange={handlePriceTypeChange}
                />
                <Space size="large" align="center">
                    <Space align="center" size={4}>
                        <Typography.Text className="form-item-label">Show Thumbnails</Typography.Text>
                        <Switch
                            checked={showThumbnail}
                            onChange={() => setShowThumbnail((prev) => !prev)}
                            disabled={!data?.length}
                        />
                    </Space>

                    <Activity mode={priceType === 1 ? 'visible' : 'hidden'}>
                        <Button type="primary" icon={<RefreshCcw size={16} />}>
                            Sync From NetSuite
                        </Button>
                    </Activity>

                    <Activity mode={priceType === 2 ? 'visible' : 'hidden'}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>
                            Add Price
                        </Button>
                    </Activity>
                </Space>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.customer_netsuite_id || record?.code}
                dataSource={data}
                pagination={false}
                scroll={{ x: 'max-content' }}
                loading={priceType === 1 ? loading.specialPrices : loading.suggestedPrices}
                components={{
                    body: {
                        cell: (props) => (
                            <td
                                {...props}
                                className={clsx({
                                    'py-0': !!data?.length && showThumbnail,
                                })}
                            />
                        ),
                    },
                }}
            />

            {isModalOpen && (
                <SuggestedPrice
                    open={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setSuggestedData(null);
                    }}
                    customer={customer}
                    data={suggestedData}
                />
            )}
        </div>
    );
};

export default CustomerDetails;
